/* ──────────────────────────────────────────────────────────────────────
 *  TemplateSelector — "New Content" template picker modal
 *
 *  Shows only unlocked templates as selectable cards. Each card displays
 *  the template icon, name, description, and trust tier badge.
 *  Selecting a template navigates to the content editor with that
 *  template pre-selected.
 *
 *  Spec reference: docs/architecture/content-architecture-spec.md §5
 *  Data source:    src/Data/Content/contentTemplates.ts
 * ────────────────────────────────────────────────────────────────────── */

import React, { useCallback, useEffect, useRef } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type {
  ContentTemplateKey,
  ContentTemplateDefinition,
} from '../../Data/Content/contentTemplates';
import styles from './TemplateSelector.module.css';

/* ── Props ────────────────────────────────────────────────────────── */

interface TemplateSelectorProps {
  /** Unlocked templates the user can choose from. */
  templates: ContentTemplateDefinition[];
  /** Called when user selects a template. */
  onSelect: (key: ContentTemplateKey) => void;
  /** Called when user dismisses the modal. */
  onClose: () => void;
}

/* ── Component ────────────────────────────────────────────────────── */

export default function TemplateSelector({
  templates,
  onSelect,
  onClose,
}: TemplateSelectorProps) {
  const { t } = useAppTranslation('content');
  const backdropRef = useRef<HTMLDivElement>(null);

  /* ---- Close on Escape ---- */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  /* ---- Close on backdrop click ---- */
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === backdropRef.current) onClose();
    },
    [onClose],
  );

  return (
    <div
      className={styles.backdrop}
      ref={backdropRef}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={t('templateSelector.title')}
    >
      <div className={styles.modal}>
        {/* ── Header ── */}
        <div className={styles.header}>
          <h2 className={styles.title}>{t('templateSelector.title')}</h2>
          <p className={styles.subtitle}>{t('templateSelector.subtitle')}</p>
          <button
            className={styles.closeButton}
            onClick={onClose}
            type="button"
            aria-label={t('templateSelector.close')}
          >
            ✕
          </button>
        </div>

        {/* ── Template grid ── */}
        <div className={styles.grid}>
          {templates.map((tmpl) => (
            <button
              key={tmpl.key}
              className={styles.card}
              onClick={() => onSelect(tmpl.key)}
              type="button"
              style={{
                '--card-accent': tmpl.bookshelf.spineColorToken,
              } as React.CSSProperties}
            >
              <div className={styles.cardAccent} />
              <span className={styles.cardIcon}>{tmpl.bookshelf.icon}</span>
              <span className={styles.cardName}>
                {t(`templates.${tmpl.key}.name`)}
              </span>
              <span className={styles.cardDescription}>
                {t(`templates.${tmpl.key}.description`)}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
